import React, { useEffect, useMemo } from 'react';
import { Button, Collapse, Empty, Flex, Form, Input, List, Modal, Space, Tag, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

import IntegrationLogo from '@/components/Integrations/IntegrationLogo';
import { deleteUserIntegrationRequest } from '@/services/api/user';
import {
  supportsUserConnection,
  supportsUserMapping,
  USER_MAPPABLE_INTEGRATION_TYPES,
} from '@/utils/integrations';
import { F, getIntlTranslation, INTL_ACTION, INTL_ENTITY } from '@/utils/intl';
import { processAPIResponseNow } from '@/utils/request';

import * as PreferencesSelectors from '@/selectors/preferencesSelectors';
import { useDispatch, useIntl, useSelector } from 'umi';

const { Panel } = Collapse;
const { Text } = Typography;

/**
 * Types this generic per-user "Id" form must never render. office365_mail
 * has no manual per-user setup by design (Office365MailIntegrationProvider
 * deliberately doesn't implement UserLevelSetupCapableInterface) — its real
 * mailbox mapping is written by the directory sync and carries fields
 * (partner_integration_id, configuration.mailbox_address) this form can't
 * see or preserve. Saving here would silently destroy that mapping; the
 * backend also refuses to touch this type as defense in depth
 * (UserService::UNMANAGED_INTEGRATION_TYPES).
 */
const UNMANAGED_INTEGRATION_TYPES = ['office365_mail'];

// connection_status values (users_integrations.connection_status) mapped to the
// tag colour + label shown per user. Anything else reads as not connected.
const CONNECTION_STATUS_TAGS = {
  connected: { color: 'success', label: 'common.integrations.status.connected' },
  pending: { color: 'processing', label: 'common.integrations.status.pending' },
  error: { color: 'error', label: 'common.integrations.status.error' },
};

type UserIntegrationRow = {
  integration_type: string;
  integration_id?: string;
  account?: string;
  connection_status?: string;
};

type UserIntegrationsProps = {
  value?: UserIntegrationRow[];
  onChange?: (value: UserIntegrationRow[]) => void;
  disabled?: boolean;
  /** The drawer's user — required for removing a stored integration. */
  userId?: string;
  /**
   * `false` when another integration surface renders below this one:
   * office365_mail is filtered out of this list by design, so a partner whose
   * only integration is that one would otherwise see "no data" directly above a
   * working Microsoft 365 panel.
   */
  showEmpty?: boolean;
};

/**
 * The integrations this section actually shows for a user — the partner's list
 * minus the types it must never render, and minus those with no per-user link
 * at all. Exported so a caller can lay out around an empty list without
 * re-implementing the exclusion rules.
 */
export const useManagedUserIntegrations = () => {
  const partnerUserIntegrations = useSelector(PreferencesSelectors.selectPartnerUserIntegrations);

  return useMemo(() => {
    const list = Array.isArray(partnerUserIntegrations) ? partnerUserIntegrations : [];

    return list.filter(
      (item) =>
        !UNMANAGED_INTEGRATION_TYPES.includes(item.type) &&
        (supportsUserMapping(item) || supportsUserConnection(item)),
    );
  }, [partnerUserIntegrations]);
};

const UserIntegrations = ({
  value = [],
  onChange,
  disabled = false,
  userId,
  showEmpty = true,
}: UserIntegrationsProps) => {
  const partnerUserIntegrations = useSelector(PreferencesSelectors.selectPartnerUserIntegrations);
  const dispatch = useDispatch();
  const intl = useIntl();
  const [activeKey, setActiveKey] = React.useState<string[]>([]);
  const [removingType, setRemovingType] = React.useState<string | null>(null);

  const managedIntegrations = useManagedUserIntegrations();

  // Integrations whose per-user link is a manually entered id — the only ones
  // editable here. Carat has no user-level concept and Trickpark maps its users
  // on its own settings page, so neither appears at all.
  const integrations = useMemo(
    () => managedIntegrations.filter(supportsUserMapping),
    [managedIntegrations],
  );

  // The calendar types: each user connects them through their own OAuth or
  // credential flow, and only for themselves (the connect endpoints are
  // self-only), so they are listed with their connection status. Removing one
  // goes through the per-type DELETE below, which disconnects server-side.
  const connections = useMemo(
    () => managedIntegrations.filter(supportsUserConnection),
    [managedIntegrations],
  );

  // Types this section owns: what it renders, plus the known manual ones so a
  // mapping for a since-deactivated integration is not dropped on the next edit.
  const editableTypes = useMemo(
    () => new Set([...USER_MAPPABLE_INTEGRATION_TYPES, ...integrations.map((item) => item.type)]),
    [integrations],
  );

  // DELETE /users/:userId/integrations/:type — the row is deleted (and a
  // calendar connection disconnected) server-side. The result is pushed into
  // the store's agent rather than through onChange: the drawer syncs form
  // changes with a full `integrations` replace, which would re-send the list
  // for nothing.
  const removeIntegration = (type: string) => {
    if (!userId) return;
    setRemovingType(type);
    deleteUserIntegrationRequest({ params: { userId, integrationType: type } })
      .then((Response) => {
        processAPIResponseNow(Response, {
          successMessage: getIntlTranslation(intl, {
            entity: INTL_ENTITY.INTEGRATION,
            action: INTL_ACTION.REMOVE_SUCCESS,
          }),
          onSuccess() {
            dispatch({
              type: 'user/fetchedAgent',
              payload: {
                id: userId,
                integrations: value.filter((item) => item.integration_type !== type),
              },
            });
          },
        }, 'data');
      })
      .finally(() => setRemovingType(null));
  };

  const confirmRemoveIntegration = (type: string) => {
    Modal.confirm({
      title: getIntlTranslation(intl, {
        entity: INTL_ENTITY.INTEGRATION,
        action: INTL_ACTION.REMOVE_CONFIRMATION,
      }),
      okButtonProps: { danger: true },
      onOk: () => removeIntegration(type),
    });
  };

  const handleChange = (index, key, newVal) => {
    // Drop rows this section does not own (calendar connections live in the
    // same relation and carry connection state we must not resubmit).
    const newList = value.filter((item) => editableTypes.has(item.integration_type));

    // An emptied id means "no longer mapped". A stored mapping is removed
    // through the DELETE endpoint; one that only exists in the form so far is
    // dropped locally. Never send the row with an empty integration_id — the
    // API rejects it (the id is `required`).
    if (!String(newVal ?? '').trim()) {
      if (userId && newList.some((item) => item.integration_type === index)) {
        removeIntegration(index);
        return;
      }
      onChange?.(newList.filter((item) => item.integration_type !== index));
      return;
    }

    const integration = newList.find(item => item.integration_type === index);
    if (!integration) {
      newList.push({ integration_type: index, [key]: newVal });
      onChange?.(newList);
      return;
    }
    onChange?.(newList.map(item => item.integration_type === index ? { ...item, [key]: newVal } : item));
  };

  useEffect(() => {
    setActiveKey(value.map(item => item.integration_type));

    if (!partnerUserIntegrations){
      dispatch({
        type: 'preferences/fetchPartnerUserIntegrations',
      })
    }
  }, [])


  const getValue = (type) => {
    const integration = value.find(item => item.integration_type === type);
    return integration ? integration.integration_id : '';
  }

  const getConnection = (type) => value.find(item => item.integration_type === type);

  const isEmpty = integrations.length === 0 && connections.length === 0;

  return (
      <Flex vertical gap="small">
        {isEmpty && showEmpty ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}/> : null}
        {integrations.length > 0 && (
          <Collapse activeKey={activeKey} onChange={setActiveKey} expandIcon={({ isActive }) => <PlusOutlined rotate={isActive ? 45 : 0} />}>
            {integrations.map((item) => (
              <Panel
                key={item.type}
                header={<Space>
                  <IntegrationLogo type={item.type} size={25} />
                  {item.display_name}
                </Space>}
              >
                <Space direction="vertical" style={{ width: '100%', padding: "0 10px" }}>
                  <Form.Item label="Id" >
                    <Input
                      allowClear
                      value={getValue(item.type)}
                      onChange={(e) => handleChange(item.type, 'integration_id', e.target.value)}
                      disabled={disabled || removingType === item.type}
                    />
                  </Form.Item>
                </Space>
              </Panel>
            ))}
          </Collapse>
        )}
        {connections.length > 0 && (
          <List
            size="small"
            bordered
            header={<Text type="secondary"><F id="common.integrations.connectedByUser" /></Text>}
            dataSource={connections}
            renderItem={(item) => {
              const connection = getConnection(item.type);
              const status = CONNECTION_STATUS_TAGS[connection?.connection_status];
              const actions = [
                <Tag key="status" color={status?.color}>
                  <F id={status?.label || 'common.integrations.status.notConnected'} />
                </Tag>,
              ];

              // Only a stored connection can be removed; the connect flow itself
              // stays with the user (self-only endpoints).
              if (connection && userId && !disabled) {
                actions.push(
                  <Button
                    key="disconnect"
                    type="link"
                    size="small"
                    danger
                    loading={removingType === item.type}
                    onClick={() => confirmRemoveIntegration(item.type)}
                  >
                    <F id="common.actions.disconnect" defaultMessage="Disconnect" />
                  </Button>,
                );
              }

              return (
                <List.Item actions={actions}>
                  <Space>
                    <IntegrationLogo type={item.type} size={20} />
                    <span>{item.display_name}</span>
                    {/* The mailbox/address the connect flow linked, when known.
                        Never integration_id — for OAuth types that is an opaque
                        provider id (see UserResource::connectedAccount). */}
                    {connection?.account && (
                      <Text type="secondary" ellipsis>{connection.account}</Text>
                    )}
                  </Space>
                </List.Item>
              );
            }}
          />
        )}
      </Flex>
  );
};

export default UserIntegrations;
